import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getLength } from '@bingads-webui-universal/primitive-utilities';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  RemainingCharsUnvalidatedTextField,
} from '@bingads-webui-campaign-react/remaining-chars-hint';

import { getTextInputContainerStyles } from './styles';

const DefaultBusinessNameMaxLength = 25;
const IS_DISTINGUISH_WIDE_CHAR = true;

export const BusinessNameWithValidation = (props) => {
  const {
    id,
    i18n,
    maxLength,
    updateValidation,
    validationProps,
    onBusinessNameChanged,
    showLabel,
  } = props;

  const { data } = validationProps;

  const activity = useContext(ActivityContext);

  const businessName = _.result(data, 'BusinessName');

  const getErrorMessages = useCallback((value) => {
    const textLength = getLength(value, IS_DISTINGUISH_WIDE_CHAR);

    if (textLength === 0) {
      return [i18n.getString(_TL_('Required field'))];
    }

    if (textLength > maxLength) {
      return [i18n.getString(_TL_('Field should not exceed {{maxLength}} characters'), { maxLength })];
    }

    return [];
  }, [i18n, maxLength]);

  useEffect(() => {
    updateValidation(getErrorMessages(businessName), { BusinessName: businessName }, false /* shouldDirty */);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChanged= useCallback((event, value) => {
    updateValidation(getErrorMessages(value), { BusinessName: value });
    onBusinessNameChanged(value);

    if (activity) {
      activity.signal(`onBusinessNameChanged - ${JSON.stringify(value)}`);
    }
  }, [updateValidation, getErrorMessages, activity, onBusinessNameChanged]);

  const classes = useDefaultStyles(getTextInputContainerStyles);

  const loadedBusinessName = useMemo(() => (
    <div className={classnames('business-name-container', classes.TextInputContainer)}>
      <RemainingCharsUnvalidatedTextField
        id={id}
        defaultValue={businessName}
        value={businessName}
        onChange={onChanged}
        maxLengthHint={maxLength}
        i18n={i18n}
        label={showLabel ? i18n.getString(_TL_('Business name')) : ''}
        ariaLabel={i18n.getString(_TL_('Business name'))}
      />
    </div>), [classes.TextInputContainer, id, businessName, onChanged, maxLength, i18n, showLabel]);

  return loadedBusinessName;
};

BusinessNameWithValidation.propTypes = {
  id: PropTypes.string,
  validationProps: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  maxLength: PropTypes.number,
  updateValidation: PropTypes.func,
  onBusinessNameChanged: PropTypes.func,
  showLabel: PropTypes.bool,
};

BusinessNameWithValidation.defaultProps = {
  id: undefined,
  maxLength: DefaultBusinessNameMaxLength,
  updateValidation: _.noop,
  onBusinessNameChanged: _.noop,
  showLabel: false,
};
